import { createApp } from 'vue'
import * as Sentry from '@sentry/vue'

import { useDjangoAuthStore } from '@/stores/authDjango'
import App from './App.vue'
import router from './router'
import vuetify from './plugins/vuetify'
import 'vuetify/dist/vuetify.min.css'

import { useApi } from '@/api/useApi'
import HeaderImage from '/header.jpeg'
import HeaderImageLazy from '/header_lazy.jpg'
import { pinia } from '@/modules/pinia'
import apiUrls from '@/api/apiUrls'

import { ComponentLibrary, useEventsBus } from '@haskoning/vue-component-library'
import {
  AuthenticationManagement,
  type MiddlewareSettingOptionsType
} from '@haskoning/vue-authentication-middleware'

import '@arcgis/map-components/dist/components/arcgis-map'
import '@arcgis/map-components/dist/components/arcgis-layer-list'
import '@arcgis/map-components/components/arcgis-basemap-gallery'
import '@arcgis/map-components/dist/components/arcgis-swipe'
import '@arcgis/map-components/components/arcgis-expand'
import '@arcgis/map-components/components/arcgis-legend'
import '@arcgis/map-components/components/arcgis-home'
import '@arcgis/map-components/components/arcgis-zoom'
import '@arcgis/map-components/components/arcgis-print'
import '@arcgis/map-components/components/arcgis-fullscreen'

pinia.use(({ store }) => {
  store.$api = api
  store.$apiUrls = apiUrls
})

const app = createApp(App).use(vuetify).use(pinia).use(router)
const { api } = useApi()

const authStore = useDjangoAuthStore(pinia)
const { bus } = useEventsBus()

app.use(ComponentLibrary, { externalBus: bus })
app
  .use<MiddlewareSettingOptionsType>(AuthenticationManagement, {
    appTitle: 'SPIN',
    appSubTitle:
      'A spatial inequality tool that highlights impacts on accessibility to resources and critical infrastructure across communities during disasters events.',
    headerImage: HeaderImage,
    apiBaseUrl: import.meta.env.VITE_APP_API_BASE_URL,
    headerImageLazy: HeaderImageLazy,
    appFrontendBaseUrl: import.meta.env.VITE_APP_FRONTEND_BASE_URL,
    useLegacyAuthentication: false,
    shouldReAuthenticate: authStore.shouldReAuthenticate,
    reAuthenticate: authStore.reAuthenticate,
    refreshToken: authStore.refreshToken
  })
  .provide('useAuthStore', authStore)

Sentry.init({
  app,
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  environment: import.meta.env.MODE,
  tracePropagationTargets: ['*'],
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
    Sentry.feedbackIntegration({
      triggerLabel: 'Feedback',
      formTitle: 'Send Feedback',
      submitButtonLabel: 'Send Feedback',
      cancelButtonLabel: 'Cancel',
      messagePlaceholder: 'What would you like to tell us?',
      successMessageText: 'Thank you for your feedback!',
      colorScheme: 'light',
      showName: true,
      showEmail: true,
      enableScreenshot: true,
      autoInject: false
    })
  ],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0
})

router.isReady().then(() => {
  app.mount('#app')

  const feedback = Sentry.getFeedback()
  if (!feedback) return

  let widget: ReturnType<typeof feedback.createWidget> | null = null

  const isAuthenticated = () => {
    return authStore.user !== null
  }

  const updateFeedbackWidget = () => {
    if (isAuthenticated() && !widget) {
      widget = feedback.createWidget()
      widget.appendToDom()
      if (authStore.user?.email) {
        Sentry.setUser({
          username: authStore.user.first_name ?? '',
          email: authStore.user.email ?? ''
        })
      }
    } else if (!isAuthenticated() && widget) {
      widget.removeFromDom()
      widget = null
      Sentry.setUser(null)
    }
  }
  updateFeedbackWidget()
  authStore.$subscribe(updateFeedbackWidget)
})
